import { Node, Position } from '@xyflow/react';
import { CSSProperties } from 'react';

import NodeData, {
  IPort,
  IEdgeData,
  EEdgeTypes,
  NodeCategoryModel,
  EPortConnectionType,
} from './node-data';
import { NodeModel } from './node-model';

export function generateNodeId() {
  const length = 4;
  let result = '';
  const characters =
    'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  const charactersLength = characters.length;
  let counter = 0;
  while (counter < length) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength));
    counter += 1;
  }
  return `${result}`;
}

export const allEdgeData = Object.keys(NodeData).reduce((acc, key) => {
  const node = NodeData[key as keyof typeof NodeData];

  if (node.ports) {
    node.ports.forEach((port) => {
      if (port.edgeData) {
        acc.push({
          id: port.id,
          nodeId: key,
          type: EEdgeTypes.CUSTOM,
          connectionType: port.connectionType,
          isPoE: port.isPoE,
          ...port.edgeData,
        });
      } else {
        acc.push({
          id: port.id,
          nodeId: key,
          label: '',
          type: EEdgeTypes.CUSTOM,
          connectionType: port.connectionType,
          isPoE: port?.isPoE,
        });
      }
    });
  }
  return acc;
}, [] as IEdgeData[]);

export const getEdgeData = (sourceHandle: string) => {
  const sourceHandleArr = sourceHandle?.split('__');
  const id = sourceHandleArr[1];
  return allEdgeData.find(
    (edge) => edge.id === id && sourceHandle.startsWith(edge.nodeId),
  );
};

export const getNodeIdFromHandle = (sourceHandle: string) => {
  const sourceHandleArr = sourceHandle.split('__');
  const id = sourceHandleArr[0];
  return id;
};

/** Get port id from handle (format: nodeId__portId__source or nodeId__portId__target). */
export const getPortIdFromHandle = (handle: string): string | undefined => {
  const parts = handle?.split('__');
  return parts && parts.length >= 2 ? parts[1] : undefined;
};

/**
 * Get port data (e.g. isPoE) from the actual node on canvas.
 * Use this for validation instead of getEdgeData when nodes have custom/instance port config.
 */
export const getPortDataFromHandle = (
  nodes: Node<NodeModel>[],
  handle: string,
): { isPoE?: boolean; connectionType?: EPortConnectionType } | undefined => {
  const nodeId = getNodeIdFromHandle(handle);
  const portId = getPortIdFromHandle(handle);
  if (!portId) return undefined;
  const node = nodes.find((n) => n.id === nodeId);
  const ports = (node?.data?.category as NodeCategoryModel)?.ports;
  const port = ports?.find((p: IPort) => p.id === portId);
  return port
    ? { isPoE: port.isPoE, connectionType: port.connectionType }
    : undefined;
};

export const calculateEdgeAnimated = (
  sourceNodeId: string,
  targetNodeId: string,
  nodes: any[],
): boolean => {
  const sourceNode = nodes.find((node) => node.id === sourceNodeId);
  const targetNode = nodes.find((node) => node.id === targetNodeId);

  return (
    sourceNode?.data?.category?.poe?.enable ||
    targetNode?.data?.category?.poe?.enable
  );
};

export const nodeScale = 0.25;

export const convertRotation = (
  rotation: number,
  data: NodeModel,
): {
  width: number;
  height: number;
  image: string;
  ports?: IPort[];
} => {
  const cloneData = JSON.parse(JSON.stringify(data)) as NodeModel;

  const isVertical = rotation % 180 === 0;
  const width = isVertical
    ? cloneData.category.viewSize.width * nodeScale
    : cloneData.category.viewSize.height * nodeScale;
  const height = isVertical
    ? cloneData.category.viewSize.height * nodeScale
    : cloneData.category.viewSize.width * nodeScale;

  let { image } = cloneData.category;
  let ports = [...(cloneData.category.ports || [])];
  if (image && cloneData.category.rotatable) {
    image = image.replace(
      `${cloneData.label}`,
      `${cloneData.label}/${rotation}`,
    );
  }

  if (cloneData.category.rotatable && ports && ports.length > 0) {
    ports = ports.map((port) => {
      if (rotation === 90) {
        let { position } = port;
        let style = {};
        if (port.position === Position.Top) {
          position = Position.Right;
          if (port.style) {
            style = convertStyle90(port.style);
          }
        } else if (port.position === Position.Right) {
          position = Position.Bottom;
          if (port.style) {
            style = convertStyle90(port.style);
          }
        } else if (port.position === Position.Bottom) {
          position = Position.Left;
          if (port.style) {
            style = convertStyle90(port.style);
          }
        } else if (port.position === Position.Left) {
          position = Position.Top;
          if (port.style) {
            style = convertStyle90(port.style);
          }
        }
        port.position = position;
        port.style = style;
      } else if (rotation === 180) {
        let { position } = port;
        let style = {};
        if (port.position === Position.Top) {
          position = Position.Bottom;
          if (port.style) {
            style = convertStyle180(port.style);
          }
        } else if (port.position === Position.Right) {
          position = Position.Left;
          if (port.style) {
            style = convertStyle180(port.style);
          }
        } else if (port.position === Position.Bottom) {
          position = Position.Top;
          if (port.style) {
            style = convertStyle180(port.style);
          }
        } else if (port.position === Position.Left) {
          position = Position.Right;
          if (port.style) {
            style = convertStyle180(port.style);
          }
        }
        port.position = position;
        port.style = style;
      } else if (rotation === 270) {
        let { position } = port;
        let style = {};
        if (port.position === Position.Top) {
          position = Position.Left;
          if (port.style) {
            style = convertStyle270(port.style);
          }
        } else if (port.position === Position.Right) {
          position = Position.Top;
          if (port.style) {
            style = convertStyle270(port.style);
          }
        } else if (port.position === Position.Bottom) {
          position = Position.Right;
          if (port.style) {
            style = convertStyle270(port.style);
          }
        } else if (port.position === Position.Left) {
          position = Position.Bottom;
          if (port.style) {
            style = convertStyle270(port.style);
          }
        }
        port.position = position;
        port.style = style;
      }
      return port;
    });
  }

  return {
    width,
    height,
    image: image || '',
    ports: ports || [],
  };
};

const convertStyle90 = (style: CSSProperties) => {
  const newStyle: any = {};
  if (style) {
    if (style.top) {
      Object.assign(newStyle, {
        left: `calc(100% - ${style.top})`,
      });
    }
    if (style.right) {
      Object.assign(newStyle, {
        bottom: style.right,
      });
    }
    if (style.bottom) {
      Object.assign(newStyle, {
        left: style.bottom,
      });
    }
    if (style.left) {
      Object.assign(newStyle, {
        top: style.left,
      });
    }
  }
  return newStyle;
};

const convertStyle180 = (style: CSSProperties) => {
  const newStyle = {};
  if (style) {
    if (style.top) {
      Object.assign(newStyle, {
        top: `calc(100% - ${style.top})`,
      });
    }
    if (style.right) {
      Object.assign(newStyle, {
        left: style.right,
      });
    }
    if (style.bottom) {
      Object.assign(newStyle, {
        top: style.bottom,
      });
    }
    if (style.left) {
      Object.assign(newStyle, {
        left: `calc(100% - ${style.left})`,
      });
    }
  }
  return newStyle;
};

const convertStyle270 = (style: CSSProperties) => {
  const newStyle = {};
  if (style) {
    if (style.top) {
      Object.assign(newStyle, {
        left: style.top,
      });
    }
    if (style.right) {
      Object.assign(newStyle, {
        top: style.right,
      });
    }
    if (style.bottom) {
      Object.assign(newStyle, {
        right: style.bottom,
      });
    }
    if (style.left) {
      Object.assign(newStyle, {
        top: `calc(100% - ${style.left})`,
      });
    }
  }
  return newStyle;
};
